import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ConnectedFacility } from '../../../../../js/api/models/ConnectedFacility';
import type { ProviderDetail } from '../../../../../js/api/models/ProviderDetail';
import { ProviderService } from '../../../../../js/api/services/ProviderService';
import { Dialog, DialogBody, DialogTitle } from '../../../components/dialog';
import { DrawerSectionHeader, DrawerField } from '../../../components/drawer-components';
import { ProviderQuestionnaireOrgView } from '../../../components/provider-questionnaire-org-view';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { formatApiDate } from '../../../utils/date';

type PresentationInfoTabProps = {
  connection: ConnectedFacility;
};

export function PresentationInfoTab({ connection }: PresentationInfoTabProps) {
  const providerId = connection.provider?.id;
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);

  const { data: providerDetail } = useQuery<ProviderDetail>({
    queryKey: ['provider', providerId],
    queryFn: () => ProviderService.providerRetrieve({ providerId: providerId! }),
    enabled: !!providerId,
  });

  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'BoardCertificationType',
    'CertificationType',
    'LicenseState',
  ]);

  const boardCertMap = codeDefinitionMaps?.BoardCertificationType || {};
  const certMap = codeDefinitionMaps?.CertificationType || {};
  const stateMap = codeDefinitionMaps?.LicenseState || {};

  // Find active license matching facility state
  const facilityState = connection.facility?.state;
  const activeLicense = providerDetail?.licenses?.find(
    (license) =>
      facilityState &&
      String(license.state) === String(facilityState) &&
      license.status === 'ACTIVE'
  );

  return (
    <div className="tw:space-y-4">
      {/* Provider Profile Link */}
      <section>
        <div className="tw:mt-2">
          <a
            href={`/app/healthcare-entity/providers/${providerId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
          >
            <i className="far fa-arrow-up-right-from-square tw:mr-2 fa-fw" />
            View Provider Profile
          </a>
        </div>
      </section>

      {/* CV */}
      {providerDetail && (
        <section>
          <div className="tw:mt-2">
            {providerDetail.cv ? (
              <a
                href={providerDetail.cv}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0"
              >
                <i className="fas fa-download tw:mr-2 fa-fw" />
                Download CV
              </a>
            ) : (
              <span className="tw:text-gray-500 tw:dark:text-gray-400">
                <i className="fas fa-file-lines tw:mr-2 fa-fw" /> No CV available
              </span>
            )}
          </div>
        </section>
      )}

      {/* Provider Questionnaire */}
      {providerId && (
        <section>
          <div className="tw:mt-2">
            <button
              type="button"
              onClick={() => setIsQuestionnaireOpen(true)}
              className="tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:hover:underline tw:dark:text-brand-green-1 tw:dark:hover:text-brand-green-0 tw:bg-transparent tw:border-0 tw:cursor-pointer tw:p-0 tw:text-left"
            >
              <i className="fas fa-clipboard-question tw:mr-2 fa-fw" />
              View Provider Questionnaire
            </button>
          </div>
        </section>
      )}

      {/* Active State License */}
      {activeLicense && (
        <section>
          <DrawerSectionHeader
            text="Active State License"
            icon={
              <i className="fas fa-id-card tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
          />
          <div className="tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-6 tw:gap-y-2 tw:text-sm">
            <DrawerField
              label="State"
              value={stateMap[activeLicense.state] || activeLicense.state}
            />
            <DrawerField label="Number" value={activeLicense.number || '--'} />
            <DrawerField
              label="Expiration Date"
              value={
                activeLicense.expiration_date
                  ? formatApiDate(activeLicense.expiration_date, { dateFormat: 'MM/dd/yyyy' })
                  : '--'
              }
            />
            <DrawerField label="IMLC" value={providerDetail?.has_imlc ? 'Yes' : 'No'} />
          </div>
        </section>
      )}

      {/* Board Certifications */}
      {providerDetail?.board_certifications && providerDetail.board_certifications.length > 0 && (
        <section>
          <DrawerSectionHeader
            text="Board Certifications"
            icon={
              <i className="fas fa-certificate tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
          />
          <div className="tw:space-y-2 tw:text-sm">
            {providerDetail.board_certifications.map((cert, index) => {
              const certType = boardCertMap[cert.type] || cert.type;
              const eligibleText = cert.board_eligible ? ' - Board Eligible' : '';
              return (
                <div key={cert.id || index} className="tw:flex tw:items-center tw:ml-5.75">
                  <span>
                    {certType} {eligibleText}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Life Support Certifications */}
      {providerDetail?.certifications && providerDetail.certifications.length > 0 && (
        <section>
          <DrawerSectionHeader
            text="Life Support Certifications"
            icon={
              <i className="fas fa-heartbeat tw:text-brand-green-0 tw:dark:text-brand-green-1 fa-fw" />
            }
          />
          <div className="tw:space-y-2 tw:text-sm">
            {providerDetail.certifications.map((cert, index) => {
              const expirationText = cert.expiration_date
                ? ` - Expires: ${formatApiDate(cert.expiration_date, { dateFormat: 'MM/dd/yyyy' })}`
                : '';
              return (
                <div key={cert.id || index} className="tw:flex tw:items-center tw:ml-5.75">
                  <span>
                    {cert.type} {expirationText}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Dialog open={isQuestionnaireOpen} onClose={() => setIsQuestionnaireOpen(false)} size="4xl">
        <div className="tw:flex tw:flex-col tw:max-h-[90vh]">
          <div className="tw:flex tw:items-start tw:justify-between tw:mb-4">
            <DialogTitle>Provider Questionnaire</DialogTitle>
            <button
              type="button"
              onClick={() => setIsQuestionnaireOpen(false)}
              className="tw:text-zinc-400 tw:hover:text-zinc-600 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:focus:ring-offset-2 tw:rounded"
            >
              <i className="fa-solid fa-xmark tw:text-lg" />
            </button>
          </div>
          <DialogBody className="tw:overflow-y-auto tw:flex-1">
            {providerId && <ProviderQuestionnaireOrgView providerId={providerId} />}
          </DialogBody>
        </div>
      </Dialog>
    </div>
  );
}
